import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  JsonHubProtocol,
  LogLevel,
} from '@microsoft/signalr';
import { getSignalRBaseUrl, LITIUM_SCOPE } from './SignalR.constants';
import { parseHubConfigs } from './SignalR.config';
import { createHubLogger } from './SignalR.logger';
import type { UpdateConnectionState } from './SignalR.types';
import { getWebSocketFromConnection } from './SignalR.internals';
import { SOCKET_UPDATE_AFTER_RECONNECT_MS } from './SignalR.constants';

export interface BuildHubConnectionOptions {
  hubName: string;
  getAccessToken: (scopes: string[]) => Promise<string | null>;
  updateConnectionState: UpdateConnectionState;
  storeSocket: (hubName: string, socket: WebSocket) => void;
  removeSocket: (hubName: string) => void;
  clearPingInterval: (hubName: string) => void;
}

export const buildHubConnection = async ({
  hubName,
  getAccessToken,
  updateConnectionState,
  storeSocket,
  removeSocket,
  clearPingInterval,
}: BuildHubConnectionOptions): Promise<HubConnection> => {
  const hubPath = parseHubConfigs().get(hubName);
  if (!hubPath) {
    throw new Error(`Hub configuration not found for: ${hubName}`);
  }

  const token = await getAccessToken([LITIUM_SCOPE]);
  if (!token) {
    throw new Error('Failed to acquire access token for SignalR');
  }

  console.log(
    `🔑 Token acquired for SignalR [${hubName}], length: ${token.length}`,
  );

  const baseUrl = getSignalRBaseUrl();
  const hubUrl = hubPath.startsWith('http') ? hubPath : `${baseUrl}${hubPath}`;

  console.log(`🔌 Building SignalR connection [${hubName}] to:`, hubUrl);

  const newConnection = new HubConnectionBuilder()
    .withUrl(hubUrl.toString(), {
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
      logger: createHubLogger(hubName),
    })
    .withAutomaticReconnect()
    .withHubProtocol(new JsonHubProtocol())
    .configureLogging(LogLevel.Trace)
    .build();

  newConnection.serverTimeoutInMilliseconds = 600_000;
  newConnection.keepAliveIntervalInMilliseconds = 15_000;

  console.log(
    `⚙️ [${hubName}] SignalR settings - Keepalive: ${newConnection.keepAliveIntervalInMilliseconds}ms (default), Server timeout: ${newConnection.serverTimeoutInMilliseconds}ms (default). Manual pings run on a separate interval.`,
  );

  newConnection.onclose((error) => {
    if (error) {
      console.error(`🔴 SignalR connection [${hubName}] closed with error:`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
        error,
      });
    } else {
      console.log(`🔴 SignalR connection [${hubName}] closed without error`);
    }

    updateConnectionState(hubName, newConnection.state);
    clearPingInterval(hubName);
    removeSocket(hubName);
  });

  newConnection.onreconnecting((error) => {
    if (error) {
      console.warn(`🔄 SignalR [${hubName}] reconnecting...`, {
        message: error.message,
        name: error.name,
        error,
      });
    } else {
      console.log(`🔄 SignalR [${hubName}] reconnecting...`);
    }
    updateConnectionState(hubName, newConnection.state);
  });

  newConnection.onreconnected((connectionId) => {
    console.log(`✅ SignalR [${hubName}] reconnected:`, connectionId);
    updateConnectionState(hubName, newConnection.state);

    setTimeout(() => {
      if (newConnection.state !== HubConnectionState.Connected) {
        return;
      }

      const socket = getWebSocketFromConnection(newConnection);
      if (socket) {
        storeSocket(hubName, socket);
        console.log(
          `✅ [${hubName}] WebSocket reference updated after reconnect (readyState: ${socket.readyState})`,
        );
      }
    }, SOCKET_UPDATE_AFTER_RECONNECT_MS);
  });

  return newConnection;
};
